import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Link as MuiLink,
  Button,
  Chip,
  Stack
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ImageCarousel from '../Carousel';
import DOMPurify from 'dompurify';
import TextImageDisplay from '../TextImageDisplay';
import ImageDisplay from '../ImageDisplay';
import CommentList from '../CommentList';
import CommentForm from '../CommentForm';
import { addComment, getBlogPostById, getCommentsForPost } from '../../firebase';
import { setSelectedPost } from '../../applicationSlice';

const BlogPost = () => {
  const dispatch = useDispatch();
  const { blogId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState({ title: '', parts: [], tags: [] });
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [error, setError] = useState(false);
  const application = useSelector((state) => state.application);

  useEffect(() => {
    if (blogId) {
      setLoading(true);
      getBlogPostById(blogId)
        .then((data) => {
          if (data) {
            setPost(data);
            setError(false);
          } else {
            setError(true);
          }
        })
        .catch((error) => {
          console.error('Error fetching post:', error);
          setError(true);
        })
        .finally(() => setLoading(false));
    }
  }, [blogId]);

  useEffect(() => {
    const loadComments = async () => {
      if (!blogId) return;
      setCommentsLoading(true);
      try {
        const data = await getCommentsForPost(blogId);
        setComments(data);
      } catch (err) {
        console.error('Error loading comments', err);
      } finally {
        setCommentsLoading(false);
      }
    };
    loadComments();
  }, [blogId]);

  const handleCommentSubmit = (commentText) => {
    const user = application?.currentUser;
    if (!user?.uid) {
      alert('Please sign in to comment.');
      navigate('/sign-in');
      return;
    }
    if (!commentText?.trim()) return;
    const displayName = user.displayName || user.email || 'Guest';

    addComment({
      postId: blogId,
      userId: user.uid,
      displayName,
      text: commentText.trim(),
    })
      .then((saved) => {
        setComments((prev) => [...prev, saved]);
      })
      .catch((err) => {
        console.error('Failed to add comment', err);
        alert('Unable to post comment right now.');
      });
  };

  const estimateReadingTime = (text) => {
    const wordsPerMinute = 200;
    const textContent = text || post.parts.map((p) => p.content).join(' ');
    const words = textContent?.split(/\s+/).length || 0;
    return Math.ceil(words / wordsPerMinute);
  };
  const handleEdit = () => {
    dispatch(setSelectedPost(post));
    navigate(`/edit-post/${post.id}`);
  };
  const renderPart = (part, index) => {
    switch (part?.type ?? 'paragraph') {
      case 'paragraph':
        return <Typography variant="body1">{part.content}</Typography>;
      case 'image':
        return <ImageDisplay part={part} />;
      case 'textImage':
        return <TextImageDisplay part={part} />;
      case 'carousel':
        return <ImageCarousel images={part.images} />;
      case 'bulletList':
        return (
          <ul>
            {part.items?.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        );
      default:
        return null;
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  if (error || !post || post.published === false) {
    return (
      <Container sx={{ textAlign: 'center', mt: 5 }}>
        <Typography variant="h5" color="error" gutterBottom>
          Blog Post Not Found
        </Typography>
        <Typography variant="body1">
          The blog post you are looking for does not exist or has been removed.
        </Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/')}>Back to Blog</Button>
      </Container>
    );
  }

  return (
    <Container>
      <Box textAlign="right" mt={1} mb={2}>
        <Button variant="contained" color="secondary" onClick={() => navigate('/blog')}>
          Back to Browse
        </Button>
      </Box>
      <Typography variant="h3" gutterBottom>{post.title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {new Date(post.created).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })} - {estimateReadingTime(post.html)} min read
      </Typography>

      {application?.currentUser?.role === 'admin' && (
        <Box textAlign="right" mt={1} mb={2}>
          <Button variant="contained" color="secondary" onClick={handleEdit}>
            Edit Post
          </Button>
        </Box>
      )}

      {post.tags?.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ my: 2 }}>
          {post.tags.map((tag, idx) => (
            <Chip key={idx} label={tag} size="small" variant="outlined" />
          ))}
        </Stack>
      )}

      <Paper elevation={4} sx={{ p: 2, mb: 4 }}>
        {post.html && (
          <Box sx={{ m: 2 }}>
            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.html) }} />
          </Box>
        )}

        {post.parts?.map((part, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            {renderPart(part, index)}
            {part.links && part.links.map((link, linkIndex) => (
              <MuiLink
                key={linkIndex}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'block', mt: 1 }}
              >
                {link.description || link.url}
              </MuiLink>
            ))}
          </Box>
        ))}
      </Paper>

      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Comments</Typography>
      <CommentList comments={comments} loading={commentsLoading} />
      {application?.currentUser?.uid ? (
        <CommentForm onCommentSubmit={handleCommentSubmit} />
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <Button variant="contained" onClick={() => navigate('/sign-in')}>
            Sign in to comment
          </Button>
          <Button variant="outlined" onClick={() => navigate('/sign-up')}>
            Create an account
          </Button>
        </Box>
      )}

      <Box textAlign="center" mt={4}>
        <Button variant="outlined" onClick={() => navigate('/blog')}>
          Back to Blog
        </Button>
      </Box>
    </Container>
  );
};

export default BlogPost;
